<script setup>
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { RouterLink, RouterView, useRoute } from 'vue-router'
import { api } from './services/api'
import { useSessionStore } from './stores/session'
import AuthDialog from './components/AuthDialog.vue'
import BrandMark from './components/BrandMark.vue'

const session = useSessionStore()
const route = useRoute()
const menuOpen = ref(false)
const header = ref(null)
const menuButton = ref(null)
let compactHeader
function closeMenu() {
  menuOpen.value = false
}
function handleOutsideClick(event) {
  if (!header.value?.contains(event.target)) closeMenu()
}
function handleEscape(event) {
  if (event.key === 'Escape' && menuOpen.value) {
    closeMenu()
    menuButton.value?.focus()
  }
}
watch(() => route.fullPath, closeMenu)
const COOKIE_NOTICE_KEY = 'promptguess-cookie-notice-v1'
const showCookieNotice = ref(false)
function dismissCookieNotice() {
  localStorage.setItem(COOKIE_NOTICE_KEY, 'dismissed')
  showCookieNotice.value = false
}
let presenceTimer
function reportPresence() {
  if (document.visibilityState === 'visible' && session.user) api('/presence', { method: 'POST' }).catch(() => {})
}
onMounted(() => {
  showCookieNotice.value = localStorage.getItem(COOKIE_NOTICE_KEY) !== 'dismissed'
  compactHeader = window.matchMedia('(max-width: 1200px)')
  session.bootstrap()
  presenceTimer = window.setInterval(reportPresence, 45_000)
  document.addEventListener('visibilitychange', reportPresence)
  document.addEventListener('pointerdown', handleOutsideClick)
  document.addEventListener('keydown', handleEscape)
  compactHeader.addEventListener('change', closeMenu)
})
onUnmounted(() => {
  window.clearInterval(presenceTimer)
  document.removeEventListener('visibilitychange', reportPresence)
  document.removeEventListener('pointerdown', handleOutsideClick)
  document.removeEventListener('keydown', handleEscape)
  compactHeader?.removeEventListener('change', closeMenu)
})
</script>

<template>
  <div class="app-shell">
    <header ref="header" class="topbar" :class="{ 'menu-open': menuOpen }">
      <RouterLink class="brand" to="/" aria-label="Промптгесс — главная"><BrandMark /> <span>Промптгесс</span></RouterLink>
      <nav id="main-navigation" aria-label="Основная навигация" @click="closeMenu">
        <RouterLink to="/how-to">Как играть</RouterLink>
        <RouterLink to="/create">Создать игру</RouterLink>
        <RouterLink to="/buy">Купить игры</RouterLink>
        <RouterLink to="/generate">Рисовать</RouterLink>
        <RouterLink to="/games">Мои игры</RouterLink>
        <RouterLink v-if="session.isAdmin" to="/admin/games">Админка</RouterLink>
      </nav>
      <RouterLink class="play-button" to="/create" @click="closeMenu">Играть <span aria-hidden="true">→</span></RouterLink>
      <RouterLink class="profile-button" :to="session.user && !session.user.guest ? '/profile' : '/login'" :aria-label="session.label" @click="closeMenu">
        <span class="profile-avatar">{{ session.label.slice(0, 1).toUpperCase() }}</span>
        <span>{{ session.label }}</span>
        <svg class="profile-chevron" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="m5 8 5 5 5-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </RouterLink>
      <button ref="menuButton" class="menu-toggle" type="button" :aria-expanded="menuOpen" aria-controls="main-navigation" :aria-label="menuOpen ? 'Закрыть меню' : 'Открыть меню'" @click="menuOpen = !menuOpen">
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path v-if="menuOpen" d="m6 6 12 12M6 18 18 6" />
          <path v-else d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>
    </header>
    <RouterView v-if="session.ready || ['home', 'how-to', 'friends', 'kids', 'solo', 'create', 'buy', 'join', 'login', 'profile', 'privacy', 'terms', 'not-found'].includes($route.name)" />
    <p v-else-if="session.error" class="session-error" role="alert">{{ session.error }} <button type="button" @click="session.bootstrap()">Повторить</button></p>
    <p v-else>Подключаемся…</p>
    <AuthDialog v-if="session.authOpen" @close="session.authOpen = false" />
    <footer class="legal-footer" aria-label="Документы сервиса">
      <nav class="legal-footer-audiences" aria-label="Для кого игра">
        <RouterLink to="/friends">Для компании</RouterLink>
        <RouterLink to="/kids">Для детей и родителей</RouterLink>
        <RouterLink to="/solo">Игра с ИИ для одного</RouterLink>
      </nav>
      <span>© Промптгесс</span>
      <span>ИП Алёхин Владимир Николаевич, ИНН 366522231695</span>
      <RouterLink to="/privacy">Политика конфиденциальности</RouterLink>
      <RouterLink to="/terms">Пользовательское соглашение</RouterLink>
      <a href="mailto:info@simulweb.ru">Связаться с нами</a>
    </footer>
    <aside v-if="showCookieNotice" class="cookie-notice" aria-label="Уведомление о cookie">
      <p>Используем cookie и данные браузера для входа, работы игры и анализа посещений через Яндекс Метрику. <RouterLink to="/privacy">Подробнее</RouterLink></p>
      <button type="button" @click="dismissCookieNotice">Понятно</button>
    </aside>
  </div>
</template>
